import React, { useState, useEffect, useMemo } from "react";
import { Error } from "../components/Error.component";
import { useGetPublishersQuery } from "../api/api";
import Spinner from "../components/Spinner";
import { Card, Space, Table, Row, Col } from "antd";
import SearchBox from "../components/SearchBox";
import { Link } from "react-router-dom";
import '../MuseDashboard.css';

export const Publishers = () => {
  const { data, error, isLoading } = useGetPublishersQuery();

  const [searchQuery, setSearchQuery] = useState("");
  const [publishers, setPublishers] = useState([]);
  
  useEffect(() => {
    if (data) {
      setPublishers(Array.from(new Set(data.data.map(item => item.publishername_0))));
    }
  }, [data]);

  const filterData = useMemo(() => {
    if (!data) return [];
    const query = searchQuery.toLowerCase();
    return data.data.filter((item) => {
      const matchesSearchQuery = Object.values(item).some((value) =>
        String(value).toLowerCase().includes(query)
      );
      return matchesSearchQuery;
    });
  }, [data, searchQuery]);

  const publisherCostData = useMemo(() => {
    if (!filterData.length) return [];
    const publisherMap = new Map();
    filterData.forEach(item => {
      const publisher = item.publishername_0;
      const cost = parseFloat(item.totallicensecostperannum_0) || 0;
      if (publisherMap.has(publisher)) {
        publisherMap.set(publisher, publisherMap.get(publisher) + cost);
      } else {
        publisherMap.set(publisher, cost);
      }
    });
    return Array.from(publisherMap, ([publisher, totalCost]) => ({ publisher, totalCost }))
      .sort((a, b) => {
        if (a.publisher && b.publisher) {
          return a.publisher.localeCompare(b.publisher);
        }
        return 0;
      }); // Sort alphabetically by publisher
  }, [filterData]);

  const totalPublishers = publisherCostData.length;

  const topPublisher = useMemo(() => {
    if (!publisherCostData.length) return null;
    return publisherCostData.reduce((prev, current) => (prev.totalCost > current.totalCost ? prev : current));
  }, [publisherCostData]);

  const topLicensesPublisher = useMemo(() => {
    if (!filterData.length) return null;
    const publisherMap = new Map();
    filterData.forEach(item => {
      const publisher = item.publishername_0;
      if (publisherMap.has(publisher)) {
        publisherMap.set(publisher, publisherMap.get(publisher) + 1);
      } else {
        publisherMap.set(publisher, 1);
      }
    });
    return Array.from(publisherMap, ([publisher, count]) => ({ publisher, count })).reduce((prev, current) => (prev.count > current.count ? prev : current));
  }, [filterData]);

  const columns = [
    { 
      title: "Publisher", 
      dataIndex: "publisher", 
      key: "publisher", 
      className: "table-cell-padding",
      sorter: (a, b) => a.publisher.localeCompare(b.publisher) 
    },
    { 
      title: "Total Annual Cost", 
      dataIndex: "totalCost", 
      key: "totalCost", 
      render: (value) => value ? `$${Math.round(value).toLocaleString()}` : '$0', 
      className: "table-cell-padding",
      sorter: (a, b) => a.totalCost - b.totalCost 
    },
    { 
      title: "Actions", 
      key: "actions", 
      render: (text, record) => <Link to={`/publishers/${record.publisher}`}>Details</Link>, 
      className: "table-cell-padding" 
    },
  ];

  return (
    <div className="montserrat-font">
      {isLoading && <Spinner />}
      {error && <Error error={"Oops!"} />}
      {data && !error && (
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          <Row gutter={16}>
            <Col span={8}>
              <Card className="muse-card equal-height-card">
                <h3>Total publishers</h3>
                <h3><p><strong>{totalPublishers}</strong></p></h3>
              </Card>
            </Col>
            <Col span={8}>
              <Card className="muse-card equal-height-card">
                <h3>Top annual cost Publisher</h3>
                {topPublisher ? (
                  <>
                    <h3><p><strong>{topPublisher.publisher}</strong></p></h3>
                    <h3><p><strong>${Math.round(topPublisher.totalCost).toLocaleString()}</strong></p></h3>
                  </>
                ) : (
                  <h3><p><strong>No data available</strong></p></h3>
                )}
              </Card>
            </Col>
            <Col span={8}>
              <Card className="muse-card equal-height-card">
                <h3>Top licenses with single Publisher</h3>
                {topLicensesPublisher ? (
                  <>
                    <h3><p><strong>{topLicensesPublisher.publisher}</strong></p></h3>
                    <h3><p><strong>{topLicensesPublisher.count} Licenses</strong></p></h3>
                  </>
                ) : (
                  <h3><p><strong>No data available</strong></p></h3>
                )}
              </Card>
            </Col>
          </Row>
          <Card className="muse-card">
            <Space direction="horizontal" size="middle" style={{ marginBottom: 16 }}>
              <SearchBox onSearch={setSearchQuery} />
            </Space>
            <Table
              columns={columns}
              dataSource={publisherCostData}
              pagination={{
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                defaultPageSize: 10,
              }}
              rowKey="publisher"
            />
          </Card>
        </Space>
      )}
    </div>
  );
};
